import React, { useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "reactstrap";
import { change, formValueSelector } from "redux-form";
import {
  MERCHANT_CARRIER_GO_LIVE,
  MERCHANT_MOBILE_VIRTUAL_NUMBER,
  MERCHANT_PROVIDER_ID,
  MERCHANT_PROVISIONING_TYPE,
} from "./MerchantForm";
import type { ThunkDispatchType } from "src/app/reducers/IMainAppState";
import { useAccessible } from "src/authentication/hooks/useAccessible";
import { MerchantAccessRight } from "src/merchants/enums/MerchantAccessRight";
import { ShortCodeForm } from "src/numbers/forms/short-code-form/ShortCodeForm";
import { DateInput } from "src/shared/forms/inputs/DateInput";
import { PhoneNoInput } from "src/shared/forms/inputs/PhoneNoInput";
import { RadioInput } from "src/shared/forms/inputs/RadioInputOld";
import { SelectInput } from "src/shared/forms/inputs/SelectInputOld";
import { Validation } from "src/shared/forms/inputs/ValidationOld";

export interface MerchantFormShortCodeNumberProvisioningProps {
  form: string;
  countryCode: string;
  canEdit?: boolean;
}

export const MerchantFormShortCodeNumberProvisioning = ({
  form,
  countryCode,
  canEdit,
}: MerchantFormShortCodeNumberProvisioningProps) => {
  const selector = formValueSelector(form);
  const isProvisioningEnabled =
    useSelector((state) => selector(state, MERCHANT_PROVISIONING_TYPE)) ===
    "short";
  const providerId = useSelector((state) =>
    selector(state, MERCHANT_PROVIDER_ID),
  );
  const mobileVirtualNumber = useSelector((state) =>
    selector(state, MERCHANT_MOBILE_VIRTUAL_NUMBER),
  );

  const dispatch = useDispatch<ThunkDispatchType>();
  useEffect(() => {
    if (canEdit && !isProvisioningEnabled) {
      dispatch(change(form, MERCHANT_MOBILE_VIRTUAL_NUMBER, null));
      dispatch(change(form, MERCHANT_PROVIDER_ID, null));
    }
  }, [canEdit, dispatch, form, isProvisioningEnabled]);

  const canEditCarrierGoLive = useAccessible(
    MerchantAccessRight.VIEW_EDIT_CARRIER_GO_LIVE,
  );

  return (
    <>
      {canEdit && (
        <Row>
          <Col sm={12}>
            <RadioInput
              inputID={MERCHANT_PROVISIONING_TYPE}
              label={
                <FormattedMessage id="merchants.form.numberProvisioning.shortCode.enabled" />
              }
              value="short"
            />
          </Col>
        </Row>
      )}
      {isProvisioningEnabled && (
        <Row className={`mt-3 ${canEdit ? "ml-1" : undefined}`}>
          <Col sm={12}>
            <ShortCodeForm
              providerInputId={MERCHANT_PROVIDER_ID}
              numberInputId={MERCHANT_MOBILE_VIRTUAL_NUMBER}
              countryCode={countryCode}
              providerId={providerId}
              mobileVirtualNumber={mobileVirtualNumber}
              SelectComponent={SelectInput}
              PhoneNoComponent={PhoneNoInput}
              ValidationComponent={Validation}
            >
              {canEditCarrierGoLive && (
                <Row>
                  <Col sm={12}>
                    <DateInput
                      inputID={MERCHANT_CARRIER_GO_LIVE}
                      label={
                        <FormattedMessage id="merchants.form.numberProvisioning.shortCode.carrierGoLive" />
                      }
                    />
                  </Col>
                </Row>
              )}
            </ShortCodeForm>
          </Col>
        </Row>
      )}
    </>
  );
};
